import React, { useRef, useState, useEffect } from "react";
import { Box } from "@mui/material";
import "../style/Crops.css";

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const MonthBarChart = ({ ranges }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(600);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const labelHeight = 20;
  const barHeight = 20;
  const rowGap = 10;
  const rowSpacing = barHeight + rowGap; // 30px per row
  const firstBarTop = 28;
  const numActivities = 7;
  const totalHeight = firstBarTop + numActivities * rowSpacing + 10; // accommodate all rows
  const monthWidth = containerWidth / 12;

  const activities = [
    { name: "Pre-Germination", color: "blue", windows: ranges.preGermination || [] },
    { name: "Direct Sowing", color: "#ffa726", windows: ranges.directSowing || [] },
    { name: "Setting", color: "#8d6e63", windows: ranges.setting || [] },
    { name: "Planting", color: "green", windows: ranges.planting || [] },
    { name: "Pruning", color: "#9c27b0", windows: ranges.pruning || [] },
    { name: "Harvesting", color: "orange", windows: ranges.harvest || [] },
    { name: "Picking", color: "#4caf50", windows: ranges.picking || [] },
  ];

  const buildBarsForInterval = (activity, rowIndex, interval, intervalIdx) => {
    const { name, color } = activity;
    const { start, end } = interval;
    const top = firstBarTop + rowIndex * rowSpacing;
    const label = `${name}: ${monthNames[start]} to ${monthNames[end]}`;
    const keyBase = `${name}-${intervalIdx}`;
    const baseStyle = {
      position: "absolute",
      top: `${top}px`,
      height: `${barHeight}px`,
      backgroundColor: color,
    };

    if (start > end) {
      return [
        <Box
          key={`${keyBase}-first`}
          className={`month-bar month-bar-${rowIndex}`}
          style={{
            ...baseStyle,
            left: `${start * monthWidth}px`,
            width: `${(12 - start) * monthWidth}px`,
          }}
          data-label={label}
          title={label}
        />,
        <Box
          key={`${keyBase}-second`}
          className={`month-bar month-bar-${rowIndex}`}
          style={{
            ...baseStyle,
            left: `0px`,
            width: `${(end + 1) * monthWidth}px`,
          }}
          data-label={label}
          title={label}
        />,
      ];
    }
    const left = start * monthWidth;
    const width = (end - start + 1) * monthWidth;
    return (
      <Box
        key={keyBase}
        className={`month-bar month-bar-${rowIndex}`}
        style={{
          ...baseStyle,
          left: `${left}px`,
          width: `${width}px`,
        }}
        data-label={label}
        title={label}
      />
    );
  };

  return (
    <Box ref={containerRef} className="month-chart" style={{ height: `${totalHeight}px` }}>
      {/* Month labels */}
      {monthNames.map((m, i) => (
        <Box
          key={m}
          className="month-label"
          style={{
            left: `${i * monthWidth}px`,
            width: `${monthWidth}px`,
            height: `${labelHeight}px`,
          }}
        >
          {m}
        </Box>
      ))}

      {/* Vertical dividers */}
      {monthNames.map((_, i) => (
        <Box
          key={`divider-${i}`}
          className="month-divider"
          style={{ left: `${i * monthWidth}px`, top: `${labelHeight}px` }}
        />
      ))}

      {/* Activity bars - one per interval per activity */}
      {activities.flatMap((activity, rowIndex) =>
        activity.windows.flatMap((interval, idx) => {
          const bars = buildBarsForInterval(activity, rowIndex, interval, idx);
          return Array.isArray(bars) ? bars : [bars];
        })
      )}
    </Box>
  );
};

export default MonthBarChart;
